import React, { useState, useMemo } from "react";
import styles from "./detail.module.scss";
import moment from "moment";
import { ViewReportModal } from "../PlayerPreview/ViewReportModal";

const PROJECTION_ORDER = [
  "HM+",
  "HM",
  "HM-",
  "MM+",
  "MM",
  "MM-",
  "LM+",
  "LM",
  "LM-",
];

const projectionToValue = (code) => {
  if (!code) return null;
  const normalized = String(code).trim().toUpperCase();
  const idx = PROJECTION_ORDER.indexOf(normalized);
  return idx === -1 ? null : PROJECTION_ORDER.length - idx;
};

export const DataVizProjectionChart = ({ playerID, playerName, reportsData, firstName, lastName }) => {
  const [selectedReportId, setSelectedReportId] = useState(null);

  const chartData = useMemo(() => {
    if (!Array.isArray(reportsData) || !playerID) return [];

    const docs = reportsData
      .filter((data) => data.playerProfile === playerID && data.type === "Scouting")
      .map((data) => {
        const pr = data.typeSpecificFields?.projectionRange || {};
        const lowLabel = pr.low || "";
        const highLabel = pr.high || "";

        const lowValue = projectionToValue(lowLabel);
        const highValue = projectionToValue(highLabel);

        if (!lowValue && !highValue) return null;

        let m = null;
        if (data.reportDate) {
          // Use moment directly on the stored value so dates
          // match the Reports table (avoids timezone shifting).
          m = moment(data.reportDate);
        }

        return {
          id: data.id,
          playerId: playerID,
          reportTitle: data.reportPreview || "(Untitled report)",
          dateLabel: m ? m.format("MM/DD/YY") : "Unknown",
          sortValue: m ? m.valueOf() : 0,
          lowLabel: lowLabel || highLabel || "",
          highLabel: highLabel || lowLabel || "",
          lowValue: lowValue || highValue || null,
          highValue: highValue || lowValue || null,
        };
      })
      .filter(Boolean);

    docs.sort((a, b) => {
      return a.sortValue - b.sortValue;
    });

    return docs;
  }, [reportsData, playerID]);

  // Simple SVG layout values
  const width = 800;
  const height = 260;
  const margin = { top: 20, right: 20, bottom: 40, left: 60 };
  // Nudge labels left so they don’t collide with markers at x = margin.left (first tick).
  const yAxisLabelX = margin.left - 30;

  const xScale = (index) => {
    if (chartData.length <= 1) return margin.left + (width - margin.left - margin.right) / 2;
    const step = (width - margin.left - margin.right) / (chartData.length - 1);
    return margin.left + index * step;
  };

  const yScale = (value) => {
    const minVal = projectionToValue("LM-");
    const maxVal = projectionToValue("HM+");
    if (!value) return height - margin.bottom;
    const t = (value - minVal) / (maxVal - minVal);
    // invert so higher projections are visually higher
    return margin.top + (1 - t) * (height - margin.top - margin.bottom);
  };

  const lowPath =
    chartData.length > 0
      ? chartData
          .map((p, i) => {
            const x = xScale(i);
            const y = yScale(p.lowValue);
            return `${i === 0 ? "M" : "L"} ${x} ${y}`;
          })
          .join(" ")
      : "";

  const highPath =
    chartData.length > 0
      ? chartData
          .map((p, i) => {
            const x = xScale(i);
            const y = yScale(p.highValue);
            return `${i === 0 ? "M" : "L"} ${x} ${y}`;
          })
          .join(" ")
      : "";

  return (
    <div className={styles.dataVizSection}>
      <div className={styles.dataVizChartWrapper}>
        {chartData.length > 0 && (
          <>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              width="100%"
              height="100%"
              className={styles.dataVizSvg}
            >
              {/* Grid */}
              {PROJECTION_ORDER.map((code) => {
                const y = yScale(projectionToValue(code));
                return (
                  <g key={code}>
                    <line
                      x1={margin.left}
                      y1={y}
                      x2={width - margin.right}
                      y2={y}
                      stroke="#f0f0f0"
                      strokeWidth="1"
                    />
                    <text
                      x={yAxisLabelX}
                      y={y + 4}
                      fontSize="10"
                      textAnchor="start"
                      fill="#555"
                    >
                      {code}
                    </text>
                  </g>
                );
              })}

              {/* X axis labels */}
              {chartData.map((p, i) => {
                const x = xScale(i);
                return (
                  <g key={p.id || i}>
                    <line
                      x1={x}
                      y1={height - margin.bottom}
                      x2={x}
                      y2={height - margin.bottom + 4}
                      stroke="#999"
                      strokeWidth="1"
                    />
                    <text
                      x={x}
                      y={height - margin.bottom + 16}
                      fontSize="10"
                      textAnchor="end"
                      transform={`rotate(-35 ${x} ${height - margin.bottom + 16})`}
                      fill="#555"
                    >
                      {p.dateLabel}
                    </text>
                  </g>
                );
              })}

              {/* Axes */}
              <line
                x1={margin.left}
                y1={margin.top}
                x2={margin.left}
                y2={height - margin.bottom}
                stroke="#999"
                strokeWidth="1.2"
              />
              <line
                x1={margin.left}
                y1={height - margin.bottom}
                x2={width - margin.right}
                y2={height - margin.bottom}
                stroke="#999"
                strokeWidth="1.2"
              />

              {/* Low range line (matte red) */}
              <path
                d={lowPath}
                fill="none"
                stroke="#c0392b"
                strokeWidth="2"
              />

              {/* High range line (matte green) */}
              <path
                d={highPath}
                fill="none"
                stroke="#27ae60"
                strokeWidth="2"
              />

              {/* Points */}
              {chartData.map((p, i) => {
                const x = xScale(i);
                const lowY = yScale(p.lowValue);
                const highY = yScale(p.highValue);

                return (
                  <g key={p.id || `pt-${i}`}>
                    <circle
                      cx={x}
                      cy={lowY}
                      r={5}
                      stroke="#c0392b"
                      strokeWidth="1"
                      fill="#ffffff"
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedReportId(p.id)}
                    />
                    <circle
                      cx={x}
                      cy={highY}
                      r={5}
                      stroke="#27ae60"
                      strokeWidth="1"
                      fill="#ffffff"
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedReportId(p.id)}
                    />
                  </g>
                );
              })}
            </svg>

            <div className={styles.dataVizLegend} aria-label="Projection range legend">
              <div className={styles.dataVizLegendItem}>
                <span
                  className={styles.dataVizLegendDash}
                  style={{ backgroundColor: "#27ae60" }}
                />
                <span>High Projection Range</span>
              </div>
              <div className={styles.dataVizLegendItem}>
                <span
                  className={styles.dataVizLegendDash}
                  style={{ backgroundColor: "#c0392b" }}
                />
                <span>Low Projection Range</span>
              </div>
            </div>
          </>
        )}
      </div>

      {selectedReportId && (
        <ViewReportModal
          show={!!selectedReportId}
          handleClose={() => setSelectedReportId(null)}
          reportData={reportsData.find((r) => r.id === selectedReportId)}
          firstName={firstName}
          lastName={lastName}
        />
      )}
    </div>
  );
};

